import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock3, Eye, RefreshCw, RotateCcw } from "lucide-react";

import api, { getStoredUser } from "../api";
import InvoiceDetail from "./InvoiceDetail";

function getCurrentUser() {
  return getStoredUser();
}

function InvoiceApprovalQueue() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState({ mode: "list", id: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const currentUser = useMemo(() => getCurrentUser(), []);
  const isAdmin = Boolean(
    currentUser?.is_admin ||
      currentUser?.role === "org_admin" ||
      (currentUser?.access_matrix || []).some((access) => access.role === "org_admin")
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/accounts/invoices/pending-approval");
      setRows(res.data.data || []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load approval queue.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      load();
    }
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
        Invoice approval queue is available to admin users only.
      </div>
    );
  }

  if (view.mode === "detail" && view.id) {
    return (
      <InvoiceDetail
        invoiceId={view.id}
        onBack={() => {
          setView({ mode: "list", id: null });
          load();
        }}
        onStatusChange={load}
      />
    );
  }

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <section className="grid gap-4 md:grid-cols-3">
        <SummaryCard icon={Clock3} label="Pending Approval" value={rows.length} tone="amber" />
        <SummaryCard
          icon={CheckCircle2}
          label="Pending Value"
          value={`Rs. ${rows.reduce((sum, row) => sum + Number(row.total_amount || 0), 0).toFixed(2)}`}
          tone="emerald"
        />
        <SummaryCard
          icon={RotateCcw}
          label="Oldest Draft Age"
          value={rows[0]?.invoice_date || "-"}
          tone="slate"
        />
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Invoice Approval Queue</h3>
            <p className="text-sm text-slate-500">Review submitted invoices before they can be sent to customers.</p>
          </div>
          <button onClick={load} className="secondary-btn"><RefreshCw size={15} /> Refresh</button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Created By</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{row.invoice_no}</div>
                    <div className="text-xs text-slate-500">{row.invoice_type}</div>
                  </td>
                  <td className="px-4 py-3">{row.customer_name || "-"}</td>
                  <td className="px-4 py-3">{row.created_by_name || "-"}</td>
                  <td className="px-4 py-3">{row.invoice_date || "-"}</td>
                  <td className="px-4 py-3 font-semibold">Rs. {Number(row.total_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">{row.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button onClick={() => setView({ mode: "detail", id: row.id })} className="table-btn"><Eye size={14} /> Review</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-slate-500">No invoices are waiting for approval.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-slate-500">Loading approval queue...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <style>{`
        .secondary-btn, .table-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(51 65 85);
        }
      `}</style>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tone }) {
  const palette = {
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };

  return (
    <div className={`rounded-3xl border p-5 shadow-sm ${palette[tone] || palette.slate}`}>
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon size={16} />
        <span>{label}</span>
      </div>
      <div className="mt-3 text-2xl font-bold">{value}</div>
    </div>
  );
}

export default InvoiceApprovalQueue;
